// apps/account-portal/src/pages/WishlistPage.tsx
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ConfirmModal, ListPagination, btnPrimary, type StatusTone } from '@redpanda/ui';
import { HandThumbUpIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { HandThumbUpIcon as HandThumbUpSolidIcon } from '@heroicons/react/24/solid';
import { wishlistApi } from '../services/api';
import { useWishlistStore, WishlistPost, WISHLIST_STATUSES } from '../stores/wishlistStore';
import { UserContext } from '../context/UserContext';
import PageFooter from '../components/PageFooter';
import PageContainer from '../components/PageContainer';
import FeedToolbar from '../components/feedback/FeedToolbar';
import PostDialog, { PostDraft } from '../components/feedback/PostDialog';
import OfficialReply from '../components/feedback/OfficialReply';
import PostHeading from '../components/feedback/PostHeading';
import { formatDateTime } from '../components/feedback/feedbackFormat';

const STATUS_TONE: Record<string, StatusTone> = { PENDING: 'neutral', IN_PROGRESS: 'accent', COMPLETED: 'success', DECLINED: 'danger' };

/**
 * 許願清單：依票數排序（同票新的在前）；投票＝「我也想要」，樣式與留言專區的按讚相同（一般使用者看得懂），訪客也能許願、投票
 * 狀態：待評估 → 開發中（可略過）→ 已完成／不採用（附原因）
 */
const WishlistPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, cachedUserId } = useContext(UserContext)!;
  const { data, query, isFetching, error, fetch, open, patch, prepend, remove } = useWishlistStore();
  const [dialog, setDialog] = useState<{ post?: WishlistPost } | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [votingId, setVotingId] = useState<string | null>(null);

  useEffect(() => { open(); }, [open]);
  useEffect(() => { if (error) toast.error(t('pages.wishlist.error_load')); }, [error, t]);

  const myId = user?.id ?? cachedUserId;
  const isMine = (post: WishlistPost) => !!myId && post.isMine;

  const handleVote = async (post: WishlistPost) => {
    if (votingId) return;
    setVotingId(post.id);
    const voted = !post.userHasVoted;
    patch(post.id, { userHasVoted: voted, voteCount: post.voteCount + (voted ? 1 : -1) }); // 樂觀更新
    try {
      const res = voted ? await wishlistApi.votePost(post.id) : await wishlistApi.unvotePost(post.id);
      patch(post.id, res.data);
    } catch {
      patch(post.id, { userHasVoted: post.userHasVoted, voteCount: post.voteCount });
      toast.error(t('pages.feedback.action_failed'));
    } finally {
      setVotingId(null);
    }
  };

  const handleSubmit = async (draft: PostDraft) => {
    try {
      if (dialog?.post) {
        const res = await wishlistApi.updatePost(dialog.post.id, { title: draft.title, content: draft.content });
        patch(dialog.post.id, { title: res.data.title, content: res.data.content });
        toast.success(t('pages.wishlist.update_success'));
      } else {
        const res = await wishlistApi.createPost({ title: draft.title, content: draft.content });
        prepend(res.data);
        toast.success(t('pages.wishlist.success_submit'));
      }
      setDialog(null);
    } catch (err: any) {
      toast.error(err.response?.status === 429 ? t('pages.feedback.rate_limited') : t('pages.wishlist.error_submit'));
      throw err;
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const id = deleteId;
    setDeleteId(null);
    remove(id);
    try {
      await wishlistApi.deletePost(id);
      fetch(); // 更新各篩選筆數；這頁刪空了會自動跳到最後一頁
    } catch {
      toast.error(t('pages.wishlist.delete_error'));
      fetch();
    }
  };

  const posts = data?.posts ?? [];
  // 分頁（照 CRM：列表上下各一個 ListPagination，只有一頁時不顯示）
  const pagination = data && { currentPage: data.page, totalPages: data.totalPages, totalItems: data.total, pageSize: data.pageSize };
  const changePage = (page: number) => {
    fetch({ page });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const filters = [
    { key: null, label: t('pages.wishlist.filter_all'), count: data ? Object.values(data.counts).reduce((a, b) => a + b, 0) : undefined },
    ...WISHLIST_STATUSES.map(s => ({ key: s, label: t(`pages.wishlist.status.${s}`), count: data?.counts[s] })),
  ];

  return (
    <PageContainer>
      {/* 標題區：與其他頁面相同位置 */}
      <div className="mb-4">
        <h1 className="text-2xl font-semibold text-fg mb-2">{t('pages.wishlist.title')}</h1>
        <p className="text-fg-secondary">{t('pages.wishlist.description')}</p>
      </div>

      {/* 以下內容置中限寬（閱讀型的留言、許願列表） */}
      <div className="max-w-[720px] mx-auto">
        <FeedToolbar
          filters={filters}
          active={query.filter}
          onFilter={filter => fetch({ filter, page: 1 })}
          onSearch={q => fetch({ q, page: 1 })}
          searchPlaceholder={t('pages.wishlist.search_placeholder')}
          action={<button type="button" onClick={() => setDialog({})} className={`${btnPrimary} shrink-0`}>{t('pages.wishlist.new_post')}</button>}
        />

        <ListPagination pagination={pagination} onPageChange={changePage} isLoading={isFetching} className="mb-3" />
        {!data ? (
          <div className="rounded-lg border border-line bg-surface divide-y divide-line" aria-busy="true">
            {[1, 2, 3].map(i => (
              <div key={i} className="space-y-2 p-5 animate-pulse motion-reduce:animate-none">
                <div className="h-4 w-1/3 rounded bg-slate-200 dark:bg-slate-700" />
                <div className="h-4 w-full rounded bg-slate-200 dark:bg-slate-700" />
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="rounded-lg border border-line bg-surface p-12 text-center text-fg-secondary">
            {query.filter || query.q ? t('pages.feedback.empty_filtered') : t('pages.wishlist.empty')}
          </div>
        ) : (
          <ul className={`rounded-lg border border-line bg-surface divide-y divide-line transition-opacity ${isFetching ? 'opacity-60' : ''}`}>
            {posts.map(post => (
              <li key={post.id} className="p-5">
                <PostHeading title={post.title} label={t(`pages.wishlist.status.${post.status}`)} tone={STATUS_TONE[post.status]} />
                <p className="mt-1.5 text-sm text-fg-secondary whitespace-pre-wrap break-words leading-relaxed">{post.content}</p>
                {post.adminReply && <OfficialReply reply={post.adminReply} repliedAt={post.adminReplyAt} />}

                {/* 底部一排與留言專區相同：作者・日期、「我也想要」（同按讚的樣式，已投為品牌色）、本人編輯刪除 */}
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-fg-muted">
                  <span>{post.authorName}<span className="mx-1.5" aria-hidden="true">·</span><span className="tabular-nums">{formatDateTime(post.createdAt)}</span></span>
                  <button
                    type="button"
                    onClick={() => handleVote(post)}
                    disabled={votingId === post.id}
                    aria-pressed={post.userHasVoted}
                    className={`inline-flex items-center gap-1 transition-colors ${post.userHasVoted ? 'text-accent' : 'hover:text-accent'}`}
                  >
                    {post.userHasVoted ? <HandThumbUpSolidIcon className="h-4 w-4" /> : <HandThumbUpIcon className="h-4 w-4" />}
                    {t('pages.wishlist.me_too')}
                    <span aria-hidden="true">·</span>
                    <span className="tabular-nums">{post.voteCount}</span>
                  </button>
                  {isMine(post) && (
                    <span className="ml-auto flex items-center gap-1">
                      <button type="button" onClick={() => setDialog({ post })} className="rounded p-1 hover:text-accent" title={t('pages.wishlist.edit')} aria-label={t('pages.wishlist.edit')}>
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button type="button" onClick={() => setDeleteId(post.id)} className="rounded p-1 hover:text-red-600 dark:hover:text-red-400" title={t('pages.wishlist.delete')} aria-label={t('pages.wishlist.delete')}>
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        <ListPagination pagination={pagination} onPageChange={changePage} isLoading={isFetching} className="mt-6" />
      </div>

      {dialog && (
        <PostDialog
          title={t(dialog.post ? 'pages.wishlist.edit_title' : 'pages.wishlist.new_post')}
          initial={dialog.post && { title: dialog.post.title, content: dialog.post.content }}
          titlePlaceholder={t('pages.wishlist.title_placeholder')}
          contentPlaceholder={t('pages.wishlist.content_placeholder')}
          authorName={user ? (user.name || t('pages.feedback.member')) : t('pages.feedback.guest')}
          onSubmit={handleSubmit}
          onClose={() => setDialog(null)}
        />
      )}

      <ConfirmModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title={t('pages.wishlist.delete_title')}
        message={t('pages.wishlist.delete_message')}
        confirmText={t('pages.wishlist.delete')}
        cancelText={t('pages.wishlist.cancel')}
        type="danger"
      />

      <PageFooter />
    </PageContainer>
  );
};

export default WishlistPage;
